"use client";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Label, Select as SelectPrimitive } from "radix-ui";
import { type ReactNode, forwardRef } from "react";
import { cn } from "../../..";

type SelectProps = SelectPrimitive.SelectProps &
	LabelProps & {
		className?: string;
		disabled?: boolean;
		placeholder?: string;
		errorComponent?: ReactNode;
	};

type LabelProps =
	| { label?: never; id?: never; title: string }
	| { label: ReactNode; id: string; title?: string };

export const RadixSelect = forwardRef<HTMLButtonElement, SelectProps>(
	function SelectRef(props, ref) {
		const {
			id,
			title,
			label,
			placeholder,
			disabled,
			className,
			children,
			errorComponent,
			...remainingProps
		} = props;

		return (
			<div className={cn(className, "flex flex-col gap-1")}>
				{!!label && !!id && (
					<Label.Root htmlFor={id} className="self-start font-bold">
						{label}
						{props.required && "*"}
					</Label.Root>
				)}

				<SelectPrimitive.Root {...remainingProps}>
					<SelectPrimitive.Trigger
						id={id}
						ref={ref}
						disabled={disabled}
						title={title ?? (typeof label === "string" ? label : undefined)}
						className={cn(
							"flex items-center justify-between rounded border border-shades p-2 text-left hover:border-primary-medium focus:outline-none focus-visible:border-primary-medium focus-visible:ring-1 focus-visible:ring-primary-medium focus-visible:ring-inset disabled:bg-shades-light motion-safe:transition-colors",
							{ "cursor-not-allowed": disabled },
							className,
						)}
					>
						<SelectPrimitive.Value
							placeholder={<span className="text-shades-dark">{placeholder}</span>}
						/>

						{disabled ? (
							<span className="my-3" />
						) : (
							<SelectPrimitive.Icon className="ml-2">
								<FontAwesomeIcon icon={faChevronDown} fixedWidth />
							</SelectPrimitive.Icon>
						)}
					</SelectPrimitive.Trigger>

					<SelectPrimitive.Portal>
						<SelectPrimitive.Content className="z-dialog rounded border border-shades bg-white">
							<SelectPrimitive.ScrollUpButton className="inline-flex justify-center py-2">
								<FontAwesomeIcon icon={faChevronUp} fixedWidth />
							</SelectPrimitive.ScrollUpButton>
							<SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
							<SelectPrimitive.ScrollDownButton className="inline-flex justify-center py-2">
								<FontAwesomeIcon icon={faChevronDown} fixedWidth />
							</SelectPrimitive.ScrollDownButton>
						</SelectPrimitive.Content>
					</SelectPrimitive.Portal>
				</SelectPrimitive.Root>

				{errorComponent}
			</div>
		);
	},
);

export const RadixSelectItem = forwardRef<HTMLDivElement, SelectPrimitive.SelectItemProps>(
	function Item(props, ref) {
		const { children, className, ...remainingProps } = props;

		return (
			<SelectPrimitive.Item
				{...remainingProps}
				ref={ref}
				className={cn(
					"relative cursor-pointer rounded rdx-state-checked:bg-shades-light/50 p-2 rdx-state-checked:text-secondary focus:outline-none focus-visible:bg-shades-light",
					{ "cursor-not-allowed bg-shades-light/50 opacity-50": props.disabled },
					className,
				)}
			>
				<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
			</SelectPrimitive.Item>
		);
	},
);
